/**
 * EquipmentSection Component
 * Dynamic equipment entries for daily logs
 * BuilderBel Construction Project Management System
 */

import { ChevronDown, ChevronRight, Plus, Trash2, Truck } from 'lucide-react';
import React from 'react';
import { Control, Controller, FieldErrors, UseFieldArrayReturn } from 'react-hook-form';
import type { EquipmentEntry, EquipmentStatus } from '../../../types/dailyLog';
import { Dropdown, Input, NumberInput, TextArea } from '../../forms';

// Common equipment types
const EQUIPMENT_TYPE_OPTIONS = [
  'Excavator',
  'Backhoe',
  'Bulldozer',
  'Loader',
  'Crane',
  'Forklift',
  'Scissor Lift',
  'Boom Lift',
  'Skid Steer',
  'Dump Truck',
  'Concrete Pump',
  'Concrete Mixer',
  'Generator',
  'Compressor',
  'Welder',
  'Scaffolding',
  'Concrete Vibrator',
  'Plate Compactor',
  'Power Tools',
  'Other',
];

const EQUIPMENT_STATUS_OPTIONS: { value: EquipmentStatus; label: string }[] = [
  { value: 'operational', label: 'Operational' },
  { value: 'maintenance', label: 'In Maintenance' },
  { value: 'idle', label: 'Idle' },
  { value: 'breakdown', label: 'Breakdown' },
];

interface EquipmentSectionProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldArray: UseFieldArrayReturn<any, 'equipment', 'id'>;
  isExpanded: boolean;
  onToggle: () => void;
  disabled?: boolean;
}

export const EquipmentSection: React.FC<EquipmentSectionProps> = ({
  control,
  errors,
  fieldArray,
  isExpanded,
  onToggle,
  disabled = false,
}) => {
  const { fields, append, remove } = fieldArray;
  const equipmentErrors = errors.equipment as unknown as FieldErrors<EquipmentEntry>[] | undefined;

  const handleAddEntry = () => {
    append({
      type: '',
      equipmentId: '',
      quantity: 1,
      hoursUsed: 8,
      operator: '',
      status: 'operational',
      notes: '',
    });
  };

  // Calculate totals
  const totalEquipment = fields.reduce((sum, _, index) => {
    const qty = (control._formValues?.equipment?.[index]?.quantity as number) || 0;
    return sum + qty;
  }, 0);

  const totalHours = fields.reduce((sum, _, index) => {
    const qty = (control._formValues?.equipment?.[index]?.quantity as number) || 0;
    const hours = (control._formValues?.equipment?.[index]?.hoursUsed as number) || 0;
    return sum + qty * hours;
  }, 0);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Section Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <Truck className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-900">Equipment</span>
          <span className="text-sm text-gray-500">
            ({fields.length} {fields.length === 1 ? 'entry' : 'entries'})
          </span>
        </div>
        <div className="flex items-center space-x-4">
          {fields.length > 0 && (
            <span className="text-sm text-gray-600">
              {totalEquipment} units / {totalHours.toFixed(1)} equipment-hours
            </span>
          )}
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-white">
          {/* Entries */}
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-4"
            >
              {/* Entry Header */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Equipment #{index + 1}</span>
                {!disabled && fields.length > 0 && (
                  <button
                    type="button"
                    onClick={() => remove(index)}
                    className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                    title="Remove entry"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Type & Equipment ID */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Controller
                  name={`equipment.${index}.type`}
                  control={control}
                  render={({ field: inputField }) => (
                    <div>
                      <Input
                        label="Equipment Type"
                        value={inputField.value}
                        onChange={inputField.onChange}
                        error={equipmentErrors?.[index]?.type?.message}
                        disabled={disabled}
                        required
                        placeholder="Select or enter type"
                        list={`equipment-type-options-${index}`}
                      />
                      <datalist id={`equipment-type-options-${index}`}>
                        {EQUIPMENT_TYPE_OPTIONS.map(type => (
                          <option key={type} value={type} />
                        ))}
                      </datalist>
                    </div>
                  )}
                />

                <Controller
                  name={`equipment.${index}.equipmentId`}
                  control={control}
                  render={({ field: inputField }) => (
                    <Input
                      label="Equipment ID/Tag"
                      value={inputField.value || ''}
                      onChange={inputField.onChange}
                      error={equipmentErrors?.[index]?.equipmentId?.message}
                      disabled={disabled}
                      placeholder="Unit number or tag"
                    />
                  )}
                />

                <Controller
                  name={`equipment.${index}.status`}
                  control={control}
                  render={({ field: inputField }) => (
                    <Dropdown
                      label="Status"
                      options={EQUIPMENT_STATUS_OPTIONS}
                      value={inputField.value}
                      onChange={inputField.onChange}
                      error={equipmentErrors?.[index]?.status?.message}
                      disabled={disabled}
                      required
                    />
                  )}
                />
              </div>

              {/* Quantity, Hours, Operator */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Controller
                  name={`equipment.${index}.quantity`}
                  control={control}
                  render={({ field: inputField }) => (
                    <NumberInput
                      label="Quantity"
                      value={inputField.value}
                      onChange={inputField.onChange}
                      error={equipmentErrors?.[index]?.quantity?.message}
                      disabled={disabled}
                      required
                      min={1}
                      max={100}
                    />
                  )}
                />

                <Controller
                  name={`equipment.${index}.hoursUsed`}
                  control={control}
                  render={({ field: inputField }) => (
                    <NumberInput
                      label="Hours Used"
                      value={inputField.value}
                      onChange={inputField.onChange}
                      error={equipmentErrors?.[index]?.hoursUsed?.message}
                      disabled={disabled}
                      required
                      min={0}
                      max={24}
                      step={0.5}
                    />
                  )}
                />

                <Controller
                  name={`equipment.${index}.operator`}
                  control={control}
                  render={({ field: inputField }) => (
                    <Input
                      label="Operator"
                      value={inputField.value || ''}
                      onChange={inputField.onChange}
                      error={equipmentErrors?.[index]?.operator?.message}
                      disabled={disabled}
                      placeholder="Operator name"
                    />
                  )}
                />
              </div>

              {/* Notes */}
              <Controller
                name={`equipment.${index}.notes`}
                control={control}
                render={({ field: inputField }) => (
                  <TextArea
                    label="Notes"
                    value={inputField.value || ''}
                    onChange={inputField.onChange}
                    error={equipmentErrors?.[index]?.notes?.message}
                    disabled={disabled}
                    rows={2}
                    placeholder="Equipment condition, maintenance notes..."
                  />
                )}
              />
            </div>
          ))}

          {/* Add Entry Button */}
          {!disabled && (
            <button
              type="button"
              onClick={handleAddEntry}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-brand-500 hover:text-brand-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus className="h-5 w-5" />
              <span>Add Equipment Entry</span>
            </button>
          )}

          {/* Empty State */}
          {fields.length === 0 && disabled && (
            <div className="text-center py-6 text-gray-500">No equipment entries recorded</div>
          )}
        </div>
      )}
    </div>
  );
};

export default EquipmentSection;
